import React from 'react';

import { Button, LinkButton } from '@eika/button';
import { ErrorCard, InfoCard } from '@eika/card';
import { Modal } from '@eika/modal';
import { RichIcon, RichIconTypes } from '@eika/rich-icon';

import applePay from '@domain/digital-wallet/components/apple-pay/ApplePay.svg';
import tapToPay from '@domain/digital-wallet/components/apple-pay/TapToPay.svg';
import useText from '@hooks/use-text';
import replaceCurlyWithJsx from '@utils/replaceCurlyWithJsx';

type Props = {
    children: React.ReactNode;
    status: 'warning' | 'success';
    statusModalState: [boolean, React.Dispatch<boolean>];
    done: () => void;
    undo: () => void;
};

const PendingCardTokenModalWrapper: React.FC<Props> = ({
    children,
    status,
    statusModalState: [openStatusModal, setOpenStatusModal],
    done,
    undo,
}) => {
    const text = useText();

    return (
        <div className="pending-card__modal-content">
            {children}
            <Modal open={openStatusModal} setOpen={setOpenStatusModal}>
                {status === 'warning' && (
                    <ErrorCard
                        data-testid="pending-card-warning-card"
                        isModal
                        description={
                            <span>
                                {replaceCurlyWithJsx(
                                    text.ifThisHappenedByMistake,
                                    <span className="apple-pay-modal__italic-text">{text.undo}</span>,
                                )}
                            </span>
                        }
                        icon={<RichIcon className="apple-pay-modal__status-icon" type={RichIconTypes.HAAND_HEIHEI} />}
                        title={text.areYouSureYouWantToCancelEnrollment}
                        className="apple-pay-modal__error-wrapper"
                    >
                        <div className="apple-pay-modal__submit-buttons">
                            <Button onClick={done} data-testid="pending-card-warning-card-close-button">
                                {text.close}
                            </Button>
                            <LinkButton onClick={undo} data-testid="pending-card-warning-card-undo-button">
                                {text.undo}
                            </LinkButton>
                        </div>
                    </ErrorCard>
                )}
                {status === 'success' && (
                    <InfoCard
                        className="apple-pay-modal__success-wrapper"
                        skipBackground
                        img={
                            <RichIcon
                                className="apple-pay-modal__status-icon"
                                maxHeight={125}
                                type={RichIconTypes.FERDIG}
                            />
                        }
                        data-testid="pending-card-success-card"
                    >
                        <InfoCard.TitleH3>{text.cardAddedToWallet}</InfoCard.TitleH3>
                        <p className="apple-pay-modal__success-description">{text.appleWalletSuccessDescription}</p>
                        <div className="apple-pay-modal__action-wrapper">
                            <img
                                style={{ '--action-img-width': '70px' } as React.CSSProperties}
                                className="apple-pay-modal__action-img"
                                src={tapToPay}
                                alt={text.tapToPay}
                            />
                            <img
                                style={{ '--action-img-width': '60px' } as React.CSSProperties}
                                className="apple-pay-modal__action-img"
                                src={applePay}
                                alt={text.applePay}
                            />
                        </div>
                        <Button data-testid="pending-card-success-button" onClick={done}>
                            {text.done}
                        </Button>
                    </InfoCard>
                )}
            </Modal>
        </div>
    );
};

export default PendingCardTokenModalWrapper;
